import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const BUSINESS_TYPES = [
  { value: "general_ops", label: "General Operations" },
  { value: "car_wash", label: "Car Wash" },
  { value: "campsite", label: "Campsite" },
  { value: "hotel", label: "Hotel" },
  { value: "automotive_parts", label: "Automotive Parts" },
  { value: "manufacturing", label: "Manufacturing" },
];

const emptyForm = (companyId = "") => ({
  company_id: companyId,
  name: "",
  owner: "",
  description: "",
  business_type: "general_ops",
});

export const BusinessForm = ({ open, onClose, onSubmit, companies = [], preselectedCompanyId, initialData, isEdit }) => {
  const [formData, setFormData] = useState(emptyForm(preselectedCompanyId || ""));

  useEffect(() => {
    setFormData(initialData ? {
      company_id: initialData.company_id || "",
      name: initialData.name || "",
      owner: initialData.owner || "",
      description: initialData.description || "",
      business_type: initialData.business_type || "general_ops",
    } : emptyForm(preselectedCompanyId || ""));
  }, [initialData, open, preselectedCompanyId]);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!formData.name.trim() || !formData.owner.trim() || !formData.company_id) return;
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase tracking-tight">{isEdit ? "Edit Business" : "Create Business"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {!isEdit && (
            <div className="space-y-2">
              <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Company *</Label>
              <Select value={formData.company_id} onValueChange={(value) => setFormData({ ...formData, company_id: value })}>
                <SelectTrigger className="rounded-none border-zinc-300">
                  <SelectValue placeholder="Select a company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map((company) => <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Business Type *</Label>
            <Select value={formData.business_type} onValueChange={(value) => setFormData({ ...formData, business_type: value })}>
              <SelectTrigger className="rounded-none border-zinc-300">
                <SelectValue placeholder="Select business type" />
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_TYPES.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Business Name *</Label>
            <Input value={formData.name} onChange={(event) => setFormData({ ...formData, name: event.target.value })} placeholder="Enter business name" className="rounded-none border-zinc-300 focus:border-zinc-900" required />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Owner *</Label>
            <Input value={formData.owner} onChange={(event) => setFormData({ ...formData, owner: event.target.value })} placeholder="Enter owner name" className="rounded-none border-zinc-300 focus:border-zinc-900" required />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-bold uppercase tracking-widest text-zinc-500">Description</Label>
            <Textarea value={formData.description} onChange={(event) => setFormData({ ...formData, description: event.target.value })} placeholder="Enter business description (optional)" className="rounded-none border-zinc-300 focus:border-zinc-900 min-h-[100px]" />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-zinc-300">Cancel</Button>
            <Button type="submit" className="bg-zinc-900 text-white hover:bg-zinc-800 rounded-none px-6 text-xs font-bold uppercase tracking-wider">{isEdit ? "Update" : "Create"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
